
import { useState, useEffect } from 'react';

export default function EmailPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const hasSeenPopup = sessionStorage.getItem('emailPopupSeen');
    
    if (!hasSeenPopup) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    sessionStorage.setItem('emailPopupSeen', 'true');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d4our99mma3c958r7pq0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={handleClose}>
        <div 
          className="bg-white rounded-lg shadow-2xl max-w-md w-full relative animate-fadeIn"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors w-8 h-8 flex items-center justify-center"
            aria-label="Close popup"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>

          <div className="p-8">
            <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#0f1e32] to-[#1a3a5c] rounded-full mx-auto mb-6">
              <i className="ri-mail-line text-3xl text-white"></i>
            </div>

            <h2 className="text-2xl font-bold text-[#0f1e32] text-center mb-3">
              Stay Informed About Your Care
            </h2>
            
            <p className="text-gray-600 text-center mb-6 text-sm leading-relaxed">
              Receive updates on specialty infusion services, insurance information, and important clinical resources directly to your inbox.
            </p>

            <form onSubmit={handleSubmit} data-readdy-form id="popup-newsletter-form">
              <div className="mb-4">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0f1e32] focus:border-transparent text-sm"
                  required
                  disabled={isSubmitting || submitStatus === 'success'}
                />
              </div>

              {submitStatus === 'success' && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-green-600 text-lg"></i>
                  <span className="text-green-700 text-sm">Thank you for subscribing!</span>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <i className="ri-error-warning-fill text-red-600 text-lg"></i>
                  <span className="text-red-700 text-sm">Please enter a valid email address.</span>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting || submitStatus === 'success'}
                className="w-full bg-[#0f1e32] text-white py-3 rounded-lg font-semibold hover:bg-[#1a3a5c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
              >
                {isSubmitting ? 'Subscribing...' : submitStatus === 'success' ? 'Subscribed!' : 'Subscribe Now'}
              </button>

              <p className="text-xs text-gray-500 text-center mt-4">
                We respect your privacy. Unsubscribe anytime.
              </p>
            </form>
          </div>

          <div className="bg-gradient-to-r from-[#0f1e32] to-[#1a3a5c] h-1 rounded-b-lg"></div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </>
  );
}
